import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, DialogContent, DialogTitle, Tab } from '@mui/material';
import DialogContext from "components/confirm-dialog-context";
import CustomDialog from 'components/custom/CustomDialog';
import useCatchApiError from 'hooks/useCatchApiError';
import { useSnackbarCustom } from "hooks/useSnackCustom";
import HeaderOuBannerDependendoDaInterpretacaoH1 from 'layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1';
import DfeService from 'pages/dfe/dfe.service';
import CustomIFrame from 'pages/Venda/Header/CustomIFrame';
import CustomLoading from 'pages/Venda/Header/CustomLoading';
import useFlowRoles from 'hooks/useFlowRoles';
import { useContext, useEffect, useState } from 'react';
import ConsignadoService from '../consignado.service';
import { FlowConsignadoImpressaoDTO } from "../impressao/consignado-impressao.model";
import ConsignadoImpressaoService from "../impressao/consignado-impressao.service";
import ConsignadoPDF from "../impressao/ConsignadoPDF";
import VisualizadorNotas, { TiposNotasTabView, VisualizadorItens } from "../visualizador-notas/VisualizadorNotas";
import { FlowConsignadoItemVisualizarDTO, FlowConsignadoVisualizarNotaBaseDTO, FlowConsignadoVisualizarNotasDTO } from './movimentacoes.model';
import { theme } from 'theme';

type Props = {
    uuidConsignado: string;
    open: boolean;
    onClose: () => void;
};

type LocalState = {
    data: FlowConsignadoVisualizarNotasDTO;
    selectedTab: TiposNotasTabView;
    loading: boolean;
    screenLoading: boolean;
    openItens: boolean;
    selectedItemItens: FlowConsignadoItemVisualizarDTO[];
};

const initialState: LocalState = {
    data: {
        notasSaida: [],
        notasDevolucao: [],
    },
    selectedTab: TiposNotasTabView.SAIDA,
    loading: true,
    screenLoading: false,
    openItens: false,
    selectedItemItens: [],
};

const tabHeaderStyle = {
    '&.Mui-selected': {
        color: theme.palette.primary.main
    }
};

const MovimentacoesDialog = (props: Props) => {
    const { canImprimir } = useFlowRoles().consignado;
    const consignadoService = ConsignadoService();
    const consignadoImpressaoService = ConsignadoImpressaoService();
    const dfeService = DfeService();
    const { throwErrorMessage } = useCatchApiError();
    const [localState, setLocalState] = useState(initialState);
    const [impressao, setImpressao] = useState(null as FlowConsignadoImpressaoDTO | null);
    const [urlDanfe, setUrlDanfe] = useState(null as string | null);
    const dialog = useContext(DialogContext);
    const { addSuccess } = useSnackbarCustom();
    const tabs: { label: string, value: TiposNotasTabView, data: FlowConsignadoVisualizarNotaBaseDTO[]; }[] = [
        {
            label: 'Consignado',
            value: TiposNotasTabView.SAIDA,
            data: localState.data.notasSaida,
        },

        {
            label: 'Devolução consignado',
            value: TiposNotasTabView.ENTRADA,
            data: localState.data.notasDevolucao,
        },

        {
            label: 'Todos',
            value: TiposNotasTabView.TODOS,
            data: [...localState.data.notasSaida, ...localState.data.notasDevolucao],
        },
    ];

    useEffect(() => {
        if (!props.open) {
            setTimeout(() => {
                setLocalState(prev => ({ ...prev, loading: true, }));
            }, 100);
            return;
        };
        consultar();
        //eslint-disable-next-line
    }, [props.open, props.uuidConsignado]);

    const consultar = () => {
        consignadoService.visualizarNotas(props.uuidConsignado)
            .then(({ data }) => {
                setLocalState(prev => ({ ...prev, data }));
            })
            .catch(err =>
                throwErrorMessage(err)
            )
            .finally(() => {
                setLocalState(prev => ({ ...prev, loading: false }));
            });
    };

    const handleClickMiniNota = (itens: FlowConsignadoItemVisualizarDTO[]) => {
        setLocalState(prev => ({ ...prev, openItens: true, selectedItemItens: itens }));
    };

    const handleDeleteNota = (item: FlowConsignadoVisualizarNotaBaseDTO) => {
        dialog.confirm({
            error: false,
            title: 'Excluir nota',
            message: `Deseja realmente continuar?`,
            onConfirm: () => {
                setLocalState(prev => ({ ...prev, loading: true, }));
                consignadoService.excluirNota(item.chaveAcesso)
                    .then(() => {
                        addSuccess({
                            id: 'nota-excluida',
                            message: 'Nota excluída com sucesso!',
                            preventDuplicate: true,
                        });
                        consultar();
                    })
                    .catch(err => {
                        throwErrorMessage(err);
                    });
            }
        });
    };

    const handleImprimir = (item: FlowConsignadoVisualizarNotaBaseDTO) => {
        setLocalState(prev => ({ ...prev, screenLoading: true }));
        consignadoImpressaoService.impressao(props.uuidConsignado, item.chaveAcesso)
            .then(({ data }) => {
                setImpressao(data);
            })
            .finally(() => {
                setLocalState(prev => ({ ...prev, screenLoading: false }));
            });
    };

    const handleImprimirDanfe = (item: FlowConsignadoVisualizarNotaBaseDTO) => {
        setLocalState(prev => ({ ...prev, screenLoading: true }));
        dfeService.danfe(item.chaveAcesso)
            .then(({ data }) => {
                setUrlDanfe(data);
            })
            .finally(() => {
                setTimeout(() => {
                    setLocalState(prev => ({ ...prev, screenLoading: false }));
                }, 200);
            });
    };

    return (
        <>
            <CustomDialog
                open={props.open}
                onClose={props.onClose}
                fullWidth
                maxWidth={'lg'}
            >

                <DialogTitle style={{ textAlign: "center" }}>
                    <HeaderOuBannerDependendoDaInterpretacaoH1
                        id='movimentacoes'
                        iconClassName='fa-solid fa-file-invoice-dollar'
                        label='Movimentações'
                        small
                    />
                </DialogTitle>

                <DialogContent>
                    <TabContext value={localState.selectedTab}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                            <TabList
                                onChange={(_, value) => {
                                    setLocalState(prev => ({
                                        ...prev,
                                        selectedTab: value,
                                    }));
                                }}
                                sx={{
                                    width: '100%',
                                    '& .MuiTabs-scroller .MuiTabs-indicator ': {
                                        backgroundColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                {tabs.map(t => {
                                    return <Tab
                                        key={t.value}
                                        sx={tabHeaderStyle}
                                        label={t.label}
                                        value={t.value}
                                    />;
                                })}
                            </TabList>
                        </Box>

                        {tabs.map(t => {
                            return <TabPanel
                                value={t.value}
                                key={t.value}
                                sx={{
                                    paddingBottom: 0,
                                }}
                            >
                                <VisualizadorNotas
                                    loading={localState.loading}
                                    tab={localState.selectedTab}
                                    data={t.data}
                                    onClickMiniNota={handleClickMiniNota}
                                    onDeleteNota={handleDeleteNota}
                                    onImprimir={canImprimir ? handleImprimir : undefined}
                                    onImprimirDanfe={canImprimir ? handleImprimirDanfe : undefined}
                                    onReload={consultar}
                                />
                            </TabPanel>;
                        })}
                    </TabContext>
                </DialogContent>
            </CustomDialog >

            <CustomDialog
                open={localState.openItens}
                onClose={() => { setLocalState(prev => ({ ...prev, openItens: false })); }}
                fullWidth
                maxWidth={'xl'}
            >
                <DialogContent>
                    <VisualizadorItens itens={localState.selectedItemItens} />
                </DialogContent>
            </CustomDialog>

            {!!impressao &&
                <ConsignadoPDF
                    dados={impressao}
                    onClose={() => {
                        setImpressao(null);
                    }}
                />
            }

            {!!urlDanfe && <CustomIFrame
                url={urlDanfe}
                onClose={() => {
                    setUrlDanfe(null);
                }}
            />}

            {localState.screenLoading && <CustomLoading />}
        </>

    );
};

export default MovimentacoesDialog;
